<template>
  <template v-if="!loading">
    <refonte-card>
      <annuaire-filters />
    </refonte-card>
    <refonte-card class="mt-[8px] !gap-0 !p-0">
      <div
        class="flex flex-wrap items-center gap-[8px] px-[12px] pb-[10px] pt-[12px]">
        <refonte-table-number
          v-bind:number="annuaire.length"
          v-bind:label="annuaire.length > 1 ? 'Contacts' : 'Contact'" />
        <IconSeparatorVertical />
        <refonte-button
          v-bind:disabled="activeEntrepriseIds.length !== 1"
          v-bind:on_click="() => setIsAddContactModalOpen(true)"
          v-bind:title="
            activeEntrepriseIds.length !== 1
              ? 'Veuillez sélectionner 1 seul établissement pour pouvoir utiliser cette fonctionnalité.'
              : undefined
          ">
          Ajouter un contact
        </refonte-button>
        <refonte-modal-add-contact
          v-bind:show="isAddContactModalOpen"
          v-bind:on_close="() => setIsAddContactModalOpen(false)"
          v-bind:on_submit="addContact" />
        <refonte-modal-info
          v-bind:show="isAlreadyExistModalOpen"
          v-bind:on_close="() => setIsAlreadyExistModalOpen(false)"
          content="Un contact existe déjà avec cette adresse email, ses informations ont été remplies automatiquement." />
      </div>
      <refonte-separator class="px-[12px]" />
      <refonte-card class="!gap-0 !p-0">
        <div class="mt-2 max-h-[70vh] overflow-y-auto">
          <annuaire-content
            v-if="!dataLoading"
            v-bind:delete_contact="deleteContact" />
          <template v-else>
            <spinner
              sizes="w-20 h-20"
              thickness="border-8" />
          </template>
        </div>
      </refonte-card>
    </refonte-card>
  </template>
  <template v-else>
    <spinner
      sizes="w-20 h-20"
      thickness="border-8" />
  </template>
  <refonte-modal-confirmation-action
    v-bind:show="isConfirmationActionDoneModalOpen"
    v-bind:on_close="closeConfirmationActionDoneModal"
    v-bind:title="confirmationActionDoneModalTitle"
    v-bind:content="confirmationActionDoneModalDescription" />
</template>

<script setup lang="ts">
  import RefonteCard from '@/components/refonte/cards/refonte-card.vue';
  import RefonteSeparator from '@/components/refonte/separators/refonte-separator.vue';
  import { onMounted, ref, watch } from 'vue';
  import Spinner from '@/components/utils/spinner.vue';
  import AnnuaireContent from '@/views/_espace/_refonte/gestion/_annuaire/annuaire-content.vue';
  import { useAnnuaireFilterStore, useAnnuaireStore } from '@/stores/annuaire';
  import { storeToRefs } from 'pinia';
  import RefonteTableNumber from '@/components/refonte/numbers/refonte-table-number.vue';
  import { useRoute, useRouter } from 'vue-router';
  import api_utils from '@/mixin/api_utils';
  import IconSeparatorVertical from '@/assets/img/refonte/icons/display/icon-separator-vertical.svg?component';
  import RefonteButton from '@/components/refonte/buttons/refonte-button.vue';
  import { useEntrepriseStore } from '@/stores/entreprise';
  import RefonteModalAddContact from '@/components/refonte/modals/refonte-modal-add-contact.vue';
  import RefonteModalInfo from '@/components/refonte/modals/refonte-modal-info.vue';
  import { API } from '@/http-common';
  import { Annuaire, AnnuaireForm } from '@/types/gestion/annuaire-types';
  import AnnuaireFilters from '@/views/_espace/_refonte/gestion/_annuaire/annuaire-filters.vue';
  import RefonteModalConfirmationAction from '@/components/refonte/modals/refonte-modal-confirmation-action.vue';
  import filters_utils from '@/mixin/utils/filters_utils';
  import { Filters } from '@/types/gestion/inscriptions-filters-types';

  const loading = ref(false);

  const entrepriseStore = useEntrepriseStore();
  const { activeAdminClientIds, activeEntrepriseIds } =
    storeToRefs(entrepriseStore);
  const annuaireFilterStore = useAnnuaireFilterStore();

  const { filters } = storeToRefs(annuaireFilterStore);

  const dataLoading = ref<boolean>(false);
  const annuaireStore = useAnnuaireStore();
  const { annuaire } = storeToRefs(annuaireStore);

  const fetchData = async () => {
    dataLoading.value = true;
    await annuaireStore.fetchAnnuaire(route.query);
    dataLoading.value = false;
  };

  const route = useRoute();
  watch(
    () => route.query,
    async () => {
      await initFilters();
    }
  );

  const initFilters = async () => {
    annuaireFilterStore.initFiltersFromQuery(route.query);
  };

  onMounted(async () => {
    await initFilters();
  });

  const router = useRouter();

  watch(filters, async () => {
    let query = getQuery(filters.value);
    if (activeAdminClientIds.value && activeAdminClientIds.value != '') {
      query = {
        ...query,
        clientIds: activeAdminClientIds.value
      };
    }
    if (filters_utils.methods.isDeepEqual(query, route.query)) {
      await fetchData();
    } else {
      await router.push({ query });
    }
  });
  const getQuery = (filters: Filters) =>
    api_utils.methods.formatParams(filters, []);

  const isAddContactModalOpen = ref<boolean>(false);
  const isAlreadyExistModalOpen = ref<boolean>(false);
  const isConfirmationActionDoneModalOpen = ref<boolean>(false);
  const confirmationActionDoneModalTitle = ref<string>();
  const confirmationActionDoneModalDescription = ref<string>();

  function setIsAddContactModalOpen(value: boolean) {
    isAddContactModalOpen.value = value;
  }
  function setIsAlreadyExistModalOpen(value: boolean) {
    isAlreadyExistModalOpen.value = value;
  }
  function closeConfirmationActionDoneModal() {
    isConfirmationActionDoneModalOpen.value = false;
    confirmationActionDoneModalDescription.value = undefined;
  }
  async function addContact(contact: AnnuaireForm) {
    const body = {
      nom: contact.nom,
      prenom: contact.prenom,
      email: contact.email
    };

    const response = await API.post(
      `client/v2/${activeEntrepriseIds.value[0]}/annuaires`,
      body
    );
    if (response.status === 200) {
      isAlreadyExistModalOpen.value = true;
    }
    if (response.status === 201) {
      isConfirmationActionDoneModalOpen.value = true;
      confirmationActionDoneModalTitle.value = 'Contact ajouté';
      confirmationActionDoneModalDescription.value = `${contact.email} a bien été ajouté à votre liste de contact(s).`;
    }
    await fetchData();
  }
  const deleteContact = async (contact: Annuaire) => {
    const { clientId, contactId, email, clientDenomination } = contact;
    const response = await API.delete(
      `client/v2/${clientId}/annuaires/contact/${contactId}`,
      {
        headers: {
          'SKIP-REDIRECTION': true
        }
      }
    );
    if (response.status === 200) {
      isConfirmationActionDoneModalOpen.value = true;
      confirmationActionDoneModalTitle.value = 'Contact supprimé';
      confirmationActionDoneModalDescription.value = `${email} a bien été supprimée des contacts de ${clientDenomination}`;
    }
    await fetchData();
  };
</script>

<style lang="scss" scoped></style>
